import { t } from "../../i18n";
import {
  Download,
  ExternalLink,
  LoaderCircle,
  Maximize2,
  X,
} from "lucide-react";
import { createPortal } from "react-dom";
import { useEffect, useRef, useState } from "react";
import { Markdown } from "../Markdown";
import {
  artifactDownloadUrl,
  artifactPreviewIsComplete,
  loadArtifactPreview,
  MAX_MARKDOWN_PREVIEW_BYTES,
  previewErrorMessage,
  releaseFullContentLoad,
  safeArtifactUrl,
  type ArtifactFullContentState,
  type ArtifactPreviewError,
} from "./artifact-utils";

function previewErrorFrom(cause: unknown): ArtifactPreviewError {
  return cause === "unauthenticated" ||
    cause === "forbidden" ||
    cause === "not-found" ||
    cause === "too-large" ||
    cause === "unavailable"
    ? cause
    : "unavailable";
}

function loadPreviewContent(
  safeUrl: string,
  isActive: () => boolean,
  onLoaded: (value: string) => void,
  onFailed: (error: ArtifactPreviewError) => void,
) {
  void loadArtifactPreview(safeUrl)
    .then((value) => {
      if (isActive()) onLoaded(value);
    })
    .catch((cause) => {
      if (isActive()) onFailed(previewErrorFrom(cause));
    });
}

export function ArtifactMarkdownPreview({
  url,
  initialContent = "",
  name = "Markdown artifact",
  downloadUrl = url,
  sizeBytes,
}: {
  url: string;
  initialContent?: string;
  name?: string;
  downloadUrl?: string;
  sizeBytes?: number;
}) {
  const [content, setContent] = useState(initialContent);
  const [loading, setLoading] = useState(!initialContent);
  const [error, setError] = useState<ArtifactPreviewError | null>(null);
  const [expanded, setExpanded] = useState(false);
  // Chat tool results only carry a truncated excerpt, so the reader tracks
  // whether it already holds the complete artifact bytes before fetching.
  const fullContentRef = useRef<ArtifactFullContentState>("idle");

  useEffect(() => {
    let active = true;
    const safeUrl = safeArtifactUrl(url);
    const safeInitialContent =
      initialContent.length <= MAX_MARKDOWN_PREVIEW_BYTES ? initialContent : "";
    fullContentRef.current = "idle";
    setContent(safeInitialContent);
    setLoading(Boolean(safeUrl) && !safeInitialContent);
    setError(
      initialContent.length > MAX_MARKDOWN_PREVIEW_BYTES ? "too-large" : null,
    );
    if (!safeUrl) {
      if (!safeInitialContent) setError("unavailable");
      return () => undefined;
    }
    if (safeInitialContent) return () => undefined;
    fullContentRef.current = "pending";
    loadPreviewContent(
      safeUrl,
      () => active,
      (value) => {
        fullContentRef.current = "done";
        setContent(value);
        setLoading(false);
      },
      (previewError) => {
        fullContentRef.current = "idle";
        setLoading(false);
        setError(previewError);
      },
    );
    return () => {
      active = false;
    };
  }, [initialContent, url]);

  useEffect(() => {
    if (!expanded) return;
    if (fullContentRef.current !== "idle") return;
    if (artifactPreviewIsComplete(initialContent, sizeBytes)) {
      fullContentRef.current = "done";
      setLoading(false);
      return;
    }
    const safeUrl = safeArtifactUrl(url);
    if (!safeUrl) return;
    let active = true;
    fullContentRef.current = "pending";
    setLoading(true);
    setError(null);
    loadPreviewContent(
      safeUrl,
      () => active,
      (value) => {
        fullContentRef.current = "done";
        setContent(value);
        setLoading(false);
      },
      (previewError) => {
        // Oversized artifacts can never be previewed, so do not retry them.
        fullContentRef.current = previewError === "too-large" ? "done" : "idle";
        setLoading(false);
        setError(previewError);
      },
    );
    return () => {
      active = false;
      // Release the in-flight marker so closing the reader mid-load does not
      // wedge the state and block the next expand from fetching the file.
      const released = releaseFullContentLoad(fullContentRef.current);
      if (released !== fullContentRef.current) setLoading(false);
      fullContentRef.current = released;
    };
  }, [expanded, initialContent, sizeBytes, url]);

  useEffect(() => {
    if (!expanded) return;
    const close = (event: KeyboardEvent) => {
      if (event.key === "Escape") setExpanded(false);
    };
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    document.addEventListener("keydown", close);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", close);
    };
  }, [expanded]);

  const previewBody = (
    <>
      {loading && !content ? (
        <div className="flex items-center gap-2 px-4 py-5 text-xs text-stone-400">
          <LoaderCircle className="animate-spin" size={14} />{" "}
          {t("Loading preview…")}
        </div>
      ) : error && !content ? (
        <div className="px-4 py-5 text-xs text-stone-500 dark:text-stone-400">
          {previewErrorMessage(error)}
        </div>
      ) : (
        <div className="max-h-72 overflow-y-auto px-4 py-3">
          <Markdown>{content || "_This Markdown file is empty._"}</Markdown>
        </div>
      )}
    </>
  );

  return (
    <div className="border-t border-stone-200/80 bg-white/70 dark:border-white/10 dark:bg-black/10">
      <div className="flex items-center justify-between gap-3 border-b border-stone-200/70 px-4 py-2 dark:border-white/10">
        <span className="text-[11px] font-semibold tracking-[0.14em] text-stone-400 uppercase">
          {t("Markdown preview")}
        </span>
        <button
          type="button"
          className="button gap-1.5 px-2 py-1 text-xs"
          onClick={() => setExpanded(true)}
          title={t("Expand Markdown preview")}
          aria-label={`Expand ${name}`}
        >
          <Maximize2 size={13} />
          <span className="hidden sm:inline">{t("Expand")}</span>
        </button>
      </div>
      {previewBody}
      {expanded &&
        createPortal(
          <div
            className="fixed inset-0 z-[100] bg-stone-950/55 p-0 sm:grid sm:place-items-center sm:p-6"
            role="dialog"
            aria-modal="true"
            aria-label={name}
            onMouseDown={(event) => {
              if (event.target === event.currentTarget) setExpanded(false);
            }}
          >
            <section className="flex h-[100dvh] w-full flex-col overflow-hidden bg-white shadow-2xl sm:h-[min(88vh,900px)] sm:max-w-5xl sm:rounded-3xl dark:bg-[#171b19]">
              <header className="flex shrink-0 items-center gap-3 border-b border-stone-200 px-5 py-4 sm:px-7 dark:border-white/10">
                <div className="min-w-0 flex-1">
                  <p className="text-[10px] font-bold tracking-[0.18em] text-lumen-600 uppercase">
                    {t("Markdown reader")}
                  </p>
                  <h2 className="truncate text-base font-bold text-stone-900 dark:text-stone-100">
                    {name}
                  </h2>
                </div>
                <div className="flex shrink-0 items-center gap-1">
                  {loading && content ? (
                    <LoaderCircle
                      className="mr-1 animate-spin text-stone-400"
                      size={15}
                      aria-hidden="true"
                    />
                  ) : null}
                  <a
                    href={safeArtifactUrl(url) || undefined}
                    target="_blank"
                    rel="noreferrer"
                    className="button p-2"
                    title={t("Open raw file")}
                    aria-label={t("Open raw file")}
                  >
                    <ExternalLink size={16} />
                  </a>
                  <a
                    href={artifactDownloadUrl(url, downloadUrl) || undefined}
                    className="button p-2"
                    title={t("Download file")}
                    aria-label={t("Download file")}
                  >
                    <Download size={16} />
                  </a>
                  <button
                    type="button"
                    className="button p-2"
                    onClick={() => setExpanded(false)}
                    title={t("Close reader")}
                    aria-label={t("Close reader")}
                  >
                    <X size={17} />
                  </button>
                </div>
              </header>
              <div className="min-h-0 flex-1 overflow-y-auto px-5 py-6 sm:px-12 sm:py-10">
                <div className="mx-auto max-w-3xl">
                  {loading && !content ? (
                    <div className="flex items-center gap-2 text-sm text-stone-400">
                      <LoaderCircle className="animate-spin" size={16} />
                      {t("Loading preview…")}
                    </div>
                  ) : (
                    <>
                      {error && (
                        <p className="mb-4 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800 dark:border-amber-900/60 dark:bg-amber-950/20 dark:text-amber-200">
                          {previewErrorMessage(error)}
                        </p>
                      )}
                      {content ? (
                        <Markdown>{content}</Markdown>
                      ) : error ? null : (
                        <Markdown>{"_This Markdown file is empty._"}</Markdown>
                      )}
                    </>
                  )}
                </div>
              </div>
            </section>
          </div>,
          document.body,
        )}
    </div>
  );
}
